import { Fragment, type ReactNode } from 'react';
import type { FolderPath } from '@norscom/nap-shared';

import { ViewPlaceholder } from './ViewPlaceholder';
import { ViewFolderReference } from './ViewReference';

export interface ViewFolderPathProps {
  /** The object's folder, with its parent chain. */
  readonly value: FolderPath | null | undefined;
  readonly placeholder?: ReactNode;
}

/**
 * The folder and its ancestors, root first, each folder once.
 *
 * NOTE: the read already cuts a cycle, but the chain is keyed by folder id, so it stops at a repeated id
 * itself rather than trust that: the parent that would close a cycle is left out, as the read leaves it out.
 */
export const folderChain = (folder: FolderPath): readonly FolderPath[] => {
  const chain: FolderPath[] = [];
  const seen = new Set<number>();
  for (let at: FolderPath | null = folder; at !== null && !seen.has(at.id); at = at.parent) {
    seen.add(at.id);
    chain.unshift(at);
  }
  return chain;
};

/**
 * A folder, as its path from the zone root ("Parent / Child"), each folder its own
 * reference so each can become a link.
 *
 * NOTE: a parent the user may not see, or one that closes a cycle, is `null` in the
 * read, so the path then starts below it rather than at the zone root.
 */
export const ViewFolderPath = ({ value, placeholder }: ViewFolderPathProps) =>
  value === null || value === undefined ? (
    <ViewPlaceholder placeholder={placeholder} />
  ) : (
    <>
      {folderChain(value).map((folder, index) => (
        <Fragment key={folder.id}>
          {index > 0 && ' / '}
          <ViewFolderReference value={folder} />
        </Fragment>
      ))}
    </>
  );
